import React from "react";
import { View, StyleSheet, ImageBackground, Text } from "react-native";

import CustomButton from "../../../common/general/CustomButton/CustomButton";
import { Certificate, GraduationCap, ThreeUsers } from "../../../svgs/common";
import { LinearGradient } from "expo-linear-gradient";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import { useAppConfig } from "../../../../AppConfigProvider";
import { Course, useMeQuery } from "../../../../graphql/generated/graphql";
import CustomProgressBar from "../../../common/general/CustomProgressBar/CustomProgressBar";
import CheckedBadge from "../../../svgs/common/CheckedBadge";

const formatCourseNameWithCode = (course) => {
  if (course?.course_code && course?.course_code.trim()) {
    return `${course?.course_code} ${course?.name}`;
  }
  return course?.name;
};

const getStatusText = (progress) => {
  if (typeof progress === "number") {
    if (progress === 0) {
      return "Not Started";
    } else if (progress > 0 && progress < 100) {
      return "In Progress";
    } else if (progress >= 100) {
      return "Complete";
    }
  }
  return "Not Started";
};

interface CatalogueCardProps {
  course: Course;
  onEnrollPress?: () => void;
  onDetailsPress?: (course: Course, name: string, preRequisitesCompleted?: boolean) => void;
  isLoading?: boolean;
  handleContinueCoursePress?: () => void;
  progress?: number;
  preRequisitesCompleted?: boolean;
}

function CatalogueCard({
  course,
  onEnrollPress,
  onDetailsPress,
  isLoading,
  handleContinueCoursePress,
  progress,
  preRequisitesCompleted,
}: CatalogueCardProps) {
  const { primaryColor } = useAppConfig();

  const { data: meData } = useMeQuery();

  const name = formatCourseNameWithCode(course);

  const userHasDegrees = meData?.me?.enrolledDegrees && meData?.me?.enrolledDegrees.length > 0;

  const courseIncludedInUserDegrees = course?.degreesHasCourse?.some((degree) =>
    degree?.usersEnrolledIn?.some((user) => user?.id === meData?.me?.id)
  );

  const degreeNames = course?.degreesHasCourse?.map((degree) => degree.name);

  const isEnrolled = course?.enrolledStudents?.some((student) => student?.id === meData?.me?.id);

  return (
    <View aria-label={`Course card ${name}`} style={styles.container}>
      <View style={{ width: "100%" }}>
        <ImageBackground source={{ uri: course?.thumbnail }} style={styles.image} />
        {userHasDegrees && courseIncludedInUserDegrees && (
          <LinearGradient
            colors={["rgb(11, 44, 164)", "rgb(84, 0, 192)"]}
            locations={[0.3, 1]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.infoBox}>
            <GraduationCap />
            <CustomText
              useTranslationText={false}
              aria-label="Degree name"
              role="header"
              aria-level="4"
              text="Degree Program"
              size="xs"
              weight="bold"
              style={{ color: "#FFFFFF" }}
            />
          </LinearGradient>
        )}
        {userHasDegrees && !courseIncludedInUserDegrees && (
          <View style={[styles.infoBox, { backgroundColor: "#FFF" }]}>
            <ThreeUsers />
            <CustomText
              useTranslationText={false}
              aria-label="Public Course"
              role="header"
              aria-level="4"
              text="Public Course"
              size="xs"
              weight="bold"
              style={{ color: "#344054" }}
            />
          </View>
        )}
      </View>
      <CustomSpacing type="vertical" size="m" />
      <View style={styles.textContainer}>
        <View style={styles.upperSection}>
          <CustomText
            useTranslationText={false}
            role="header"
            aria-level="1"
            weight="ultraBold"
            text={name}
            size="m"
            style={{ color: "#101828" }}
          />
          <CustomSpacing type="vertical" size={degreeNames && degreeNames.length > 0 ? "xs" : "s"} />
          {degreeNames && degreeNames.length > 0 && !isEnrolled && (
            <>
              <View style={styles.degreeContainer}>
                <Certificate />
                <CustomText
                  useTranslationText={false}
                  role="header"
                  aria-level="2"
                  weight="bold"
                  text={`${degreeNames.join(", ")} `}
                  size="s"
                  style={{ color: "#344054" }}
                />
              </View>
              <CustomSpacing type="vertical" size="s" />
            </>
          )}
          {!!course?.cost && course?.cost > 0 && !isEnrolled && (
            <>
              <CustomText
                useTranslationText={false}
                role="header"
                aria-level="2"
                weight="ultraBold"
                text={`$${course?.cost} `}
                size="l"
                style={{ color: "#000" }}
              />
              <CustomSpacing type="vertical" size="s" />
            </>
          )}
          {!isEnrolled && (
            <CustomText
              useTranslationText={false}
              role="text"
              aria-level="3"
              weight="normal"
              text={course?.description}
              size="s"
              style={{ color: "#667085", height: 60 }}
              numberOfLines={3}
              ellipsizeMode="tail"
            />
          )}
          {isEnrolled && (
            <View style={styles.progressContainer}>
              <View style={styles.progressStatus}>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                  {progress >= 100 && <CheckedBadge />}
                  <Text style={styles.statusText}>{getStatusText(progress)}</Text>
                </View>
              </View>
              <CustomProgressBar ariaLabel="Course progress bar" progress={progress} color={primaryColor} />
            </View>
          )}
        </View>
        <View style={styles.lowerSection}>
          {onDetailsPress && !isEnrolled && (
            <CustomButton
              text="Course Details"
              styleType="primaryTransparent"
              onPress={() => onDetailsPress(course, name, preRequisitesCompleted)}
              textStyle={styles.detailsButtonText}
              style={{ paddingHorizontal: 0, paddingVertical: 6 }}
            />
          )}
          {onEnrollPress && !isEnrolled && (
            <CustomButton
              text={preRequisitesCompleted ? "Enroll" : "Pre-Req Not Met"}
              onPress={onEnrollPress}
              style={[{ backgroundColor: preRequisitesCompleted ? primaryColor : "#667085" }, styles.enrollButtonStyle]}
              textStyle={styles.enrollButtonText}
              disabled={isLoading || !preRequisitesCompleted}
            />
          )}
          {isEnrolled && (
            <CustomButton
              text={progress === 0 ? "Begin Course" : "Continue Course"}
              onPress={handleContinueCoursePress}
              style={[{ backgroundColor: primaryColor }, styles.enrollButtonStyle]}
              textStyle={styles.enrollButtonText}
            />
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 24,
    overflow: "hidden",
    maxWidth: 570,
    minWidth: 400,
    width: "100%",
    borderColor: "#EAECF0",
    borderWidth: 1,
  },
  lowerSection: {
    width: "100%",
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopColor: "#EAECF0",
    borderTopWidth: 1,
    paddingTop: 16,
  },
  image: {
    width: "100%",
    height: 200,
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: 24,
    paddingBottom: 24,
    backgroundColor: "white",
    flex: 1,
  },
  upperSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    width: "100%",
  },
  enrollButtonText: {
    color: "white",
    fontWeight: "700",
  },
  enrollButtonStyle: {
    padding: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  detailsButtonText: {
    fontWeight: "700",
  },
  infoBox: {
    position: "absolute",
    top: 11,
    left: 14,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 100,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  degreeContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  progressContainer: {
    width: "100%",
    paddingVertical: 20,
    paddingBottom: 24,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    gap: 12,
  },
  progressStatus: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#667085",
  },
});

export default CatalogueCard;
